import './VoucherDetailsModal.scss'

import { Trans, useLingui } from '@lingui/react'
import { Origin } from '@redteclab/api/clients/bully'
import classNames from 'clsx'
import { arrayOf, bool } from 'prop-types'
import type { FunctionComponent, ReactElement } from 'react'

import { SA_SELLER_ID } from 'shared/consts'
import {
  PRESCRIPTION_TYPE_FREE,
  PRESCRIPTION_TYPE_INSURANCE,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
} from 'shared/consts/productTemplates'
import { nowVoucher } from 'shared/consts/vouchertypes'
import type {
  CartControllerCartEntryItemV2,
  CartControllerCartSellerDiscountV2,
  CartControllerGroupEntryV2,
  CartControllerSellerGroupV2,
} from 'types/api/cart'
import ImageRow from 'views/components/molecules/ImageRow'
import Modal from 'views/components/molecules/Modal'
import { imageItems } from 'views/components/organisms/Cart/helper/imageItems'
import CostDetail from 'views/components/organisms/CostDetail'
import SellerInfoHeader from 'views/components/organisms/SellerInfoHeader'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import { cartDiscounts, i18nType, sellerGroup } from 'views/utils/propTypes'

const PropTypes = {
  voucher: cartDiscounts.isRequired,
  sellers: arrayOf(sellerGroup),
  open: bool,
  i18n: i18nType.isRequired,
}

export type VoucherDetailsModalProps = {
  voucher: CartControllerCartSellerDiscountV2
  sellers: CartControllerSellerGroupV2[]
  open: boolean
  disablesOnShowModal?: boolean
  id: string
}

const allowedPrescriptionTypes = [
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRESCRIPTION_TYPE_INSURANCE,
  PRESCRIPTION_TYPE_FREE,
]

const affectedProductIdsArraysAreEqual = (
  source: string[],
  target: string[]
) => {
  if (!Array.isArray(source) || !Array.isArray(target)) {
    return false
  }
  if (source === target) return true
  if (source.length !== target.length) return false

  const sourceSet = new Set(source)
  const targetSet = new Set(target)

  for (const item of sourceSet) {
    if (!targetSet.has(item)) {
      return false
    }
  }

  return true
}

// there are cases when different products from the same seller
// need to be shipped through different methods. in these cases
// the (seller) suborder will be split in multiple suborders.
// if a promotion is applied to that seller, the voucher code
// might appear in all the suborders but it will have the same
// voucher code, therefore it is no longer unique.
// to be sure that we match the voucher to the right suborder,
// we need to compare the affected products, which are correctly
// and uniquely distributed within the suborders.
const voucherMatchesInCaseOfDifferentLogisticClasses = (
  sourceVoucher: CartControllerCartSellerDiscountV2,
  targetVoucher: CartControllerCartSellerDiscountV2,
  seller: CartControllerSellerGroupV2
) => {
  if (!seller.sellerGroupId) {
    return true
  }

  return affectedProductIdsArraysAreEqual(
    sourceVoucher.affectedProductIds,
    targetVoucher.affectedProductIds
  )
}

const VoucherDetailsModal: FunctionComponent<VoucherDetailsModalProps> = ({
  voucher,
  sellers,
  open,
  disablesOnShowModal,
  id,
}): ReactElement => {
  const { i18n } = useLingui()
  return (
    <Modal
      id={id}
      size="medium"
      title={
        voucher.origin === Origin.Mirakl
          ? i18n._('seller.promotion.discount.extraDiscount')
          : voucher.code
      }
      open={open}
      isClientSideControlled
      disablesOnShowModal={disablesOnShowModal}
    >
      {voucher.code !== nowVoucher &&
        sellers?.map((seller, idx) => {
          // Get Products for this seller
          const groupEntries: CartControllerGroupEntryV2[] =
            seller.groupEntries.filter(entry =>
              allowedPrescriptionTypes.includes(entry.type)
            )
          // there is only one possible entry in the eRx array
          const hasERxItems =
            seller.groupEntriesERx.length > 0 &&
            seller.groupEntriesERx[0].items.length > 0

          // Check if the seller has the voucher in the modal context
          const foundVoucher: CartControllerCartSellerDiscountV2 | undefined =
            seller.discounts.find(
              discount =>
                discount.code === voucher.code &&
                voucherMatchesInCaseOfDifferentLogisticClasses(
                  voucher,
                  discount,
                  seller
                )
            )
          const hasSameVoucher: boolean = foundVoucher?.code === voucher.code
          const aggregatedAffectedProductIds: string[] = [
            ...voucher.affectedProductIds,
          ]
          // Merge affectedProductIds by voucher in context with seller
          if (hasSameVoucher && foundVoucher) {
            aggregatedAffectedProductIds.push(
              ...foundVoucher.affectedProductIds
            )
          }

          // Check if products exist and then filter for products affected by voucher
          const products: CartControllerCartEntryItemV2[] =
            groupEntries.flatMap(entry => entry.items)

          if (products.length === 0 && !hasERxItems) return null

          const voucherAffectedProducts: CartControllerCartEntryItemV2[] =
            products.filter(product =>
              aggregatedAffectedProductIds.includes(product.offerId)
            )

          // Map data for use in ImageRow
          const imageSeller = {
            ...seller,
            groupEntries: [
              {
                type: '',
                items: voucherAffectedProducts,
              },
            ],
            groupEntriesERx: seller.groupEntriesERx,
          }

          const voucherItemWrapper = classNames(
            'l-flex l-flex--distribute-space',
            {
              'l-flex--vertical l-flex--start': hasSameVoucher,
            }
          )

          const sellerWrapper = classNames(
            'u-border-radius u-background-color--background u-padding-large',
            {
              'u-margin--bottom': sellers.length > 1 && idx !== sellers.length,
            }
          )

          const shouldShowMarketplaceTexts =
            hasSameVoucher &&
            voucher.origin === Origin.Mirakl &&
            seller.sellerId !== SA_SELLER_ID

          const shouldShowNotAppliedReasonForMarketplacePromotion =
            !hasSameVoucher && voucher.origin === Origin.Mirakl

          return (
            <div
              key={`${seller.sellerName}-${seller.sellerId}`}
              className={sellerWrapper}
            >
              <div className={voucherItemWrapper}>
                <SellerInfoHeader
                  idx={idx + 1}
                  max={sellers.length}
                  sellerName={seller.sellerName}
                  disabled={!hasSameVoucher}
                  isMarketplacePromotion={shouldShowMarketplaceTexts}
                />
                {hasSameVoucher && (
                  <ImageRow items={imageItems(imageSeller, hasERxItems)} />
                )}
                {!hasSameVoucher && (
                  <span className="u-color--negative u-font-weight--bold">
                    {shouldShowNotAppliedReasonForMarketplacePromotion ? (
                      <Trans id="seller.promotion.discount.notApplied" />
                    ) : (
                      <Trans id="voucherDetails.modal.info" />
                    )}
                  </span>
                )}
              </div>
              {hasSameVoucher && (
                <>
                  <hr className="o-VoucherDetailsModal__hr u-margin-large--bottom u-margin-large--top u-border--bottom u-border-color--grey" />
                  <CostDetail
                    amount={voucher.value}
                    totalAmount={seller.subAmount}
                    name={voucher.code}
                  />
                </>
              )}
            </div>
          )
        })}
      {voucher.code === nowVoucher && (
        <TransWithHtml
          text={i18n._('cart.modal.voucher.now', {
            now_vouchercode: voucher.code,
          })}
          element="p"
        />
      )}
    </Modal>
  )
}

VoucherDetailsModal.propTypes = PropTypes

VoucherDetailsModal.defaultProps = {
  sellers: [],
  open: false,
}

export * from './VoucherDetailsModalConnected'
export default VoucherDetailsModal
